import PageHero from '../components/aravi/PageHero';
import Enquiry from '../components/aravi/Enquiry';
import { CONTACT_PAGE } from '../constants/testIds';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Clock, ArrowUpRight, Download } from 'lucide-react';
import { PROJECT } from '../data/content';

const OFFICES = [
  {
    city: 'Hyderabad',
    label: 'Corporate Office',
    address: 'Flat No. 303, Cyber Residency, Indra Nagar, Gachibowli, Serilingampally, K.V. Rangareddy — 500032, Telangana',
    phones: ['+91 94919 29527', '+91 94949 49527'],
    email: 'sales@araviecohomes.com',
    hours: 'Mon — Sat · 10:00 to 19:00',
  },
  {
    city: 'Ibrahimpatnam',
    label: 'Site Office',
    address: 'Aravi Eco Homes site, opposite Telangana Araku Resort · 17.089, 78.743',
    phones: ['+91 94919 29527'],
    email: 'sales@araviecohomes.com',
    hours: 'Site visits by appointment',
    hasQr: true,
  },
];

export default function ContactPage() {
  return (
    <div data-testid={CONTACT_PAGE.root}>
      <PageHero
        eyebrow="Begin a Conversation"
        title={<>Walk the land<br/>with us.</>}
        intro="A double road runs from ORR Exit straight to our gate — fifteen minutes. Schedule a visit, and we will meet you on site."
        image="https://images.unsplash.com/photo-1757359056339-22968344cce6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBmb3Jlc3QlMjBob21lJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MjczMTIzNnww&ixlib=rb-4.1.0&q=85"
      />

      {/* Offices */}
      <section className="bg-[#F5F1E8] py-20 lg:py-24">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
            {OFFICES.map((o, i) => (
              <motion.div
                key={o.city}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="border-t border-[#C9A458]/30 pt-8"
              >
                <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
                  {o.label}
                </span>
                <h3 className="font-serif text-[#18342A] text-4xl md:text-5xl mt-3 leading-tight font-light">
                  {o.city}
                </h3>
                <div className="mt-8 space-y-5">
                  <Row icon={<MapPin size={16} strokeWidth={1.4} />} label="Address" value={o.address} />
                  {o.phones.map((p, idx) => (
                    <Row
                      key={p}
                      icon={<Phone size={16} strokeWidth={1.4} />}
                      label={idx === 0 ? 'Telephone' : 'Alternate'}
                      value={p}
                      href={`tel:${p.replace(/\s/g, '')}`}
                    />
                  ))}
                  <Row icon={<Mail size={16} strokeWidth={1.4} />} label="Write to us" value={o.email} href={`mailto:${o.email}`} />
                  <Row icon={<Clock size={16} strokeWidth={1.4} />} label="Hours" value={o.hours} />
                </div>

                {o.hasQr && (
                  <div className="mt-8 pt-8 border-t border-[#C9A458]/25 flex items-start gap-5">
                    <div className="p-2.5 bg-[#F5F1E8] border border-[#C9A458]/30 shrink-0">
                      <img
                        src="/qr/site-location.png"
                        alt="QR code — Aravi site location"
                        data-testid="contact-office-qr"
                        className="block w-24 h-24"
                      />
                    </div>
                    <div className="pt-1">
                      <div className="text-[10px] tracking-[0.4em] uppercase text-[#A66B45]">
                        Scan for Directions
                      </div>
                      <p className="mt-2 text-[#6D6A61] text-sm font-light leading-relaxed max-w-xs">
                        Open the exact site pin on Google Maps from your phone camera.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Maps card */}
          <div className="mt-20 border-t border-[#C9A458]/30 pt-12">
            <div className="grid grid-cols-12 gap-8 lg:gap-16 items-start">
              <div className="col-span-12 lg:col-span-7">
                <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
                  Site Location
                </span>
                <h3 className="font-serif text-[#18342A] text-3xl md:text-4xl lg:text-5xl mt-4 leading-tight font-light">
                  17.089°N · 78.743°E
                </h3>
                <p className="mt-6 text-[#6D6A61] text-base font-light max-w-lg">
                  25 acres in Ibrahimpatnam, opposite the Telangana Araku Resort
                  and ringed by the Osmania Geological Observatory (forest). A
                  double-road from ORR Exit reaches the gate in fifteen minutes.
                </p>
                <a
                  href={PROJECT.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group mt-8 inline-flex items-center gap-3 bg-[#18342A] text-[#F5F1E8] px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#C9A458] hover:text-[#18342A] transition-colors"
                >
                  Open on Google Maps
                  <ArrowUpRight size={14} strokeWidth={1.6} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </div>

              {/* QR code panel */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                data-testid="contact-qr-panel"
                className="col-span-12 lg:col-span-5 lg:pl-6"
              >
                <div className="bg-[#18342A] text-[#F5F1E8] p-8 lg:p-10 grain-overlay relative overflow-hidden">
                  <div className="relative z-10 flex flex-col items-center text-center">
                    <span className="text-[10px] tracking-[0.4em] uppercase text-[#C9A458]">
                      Scan for Directions
                    </span>
                    <h4 className="font-serif text-2xl md:text-3xl mt-4 leading-tight font-light">
                      Point your camera<br />at the code.
                    </h4>
                    <div className="mt-8 p-4 bg-[#F5F1E8] w-fit">
                      <img
                        src="/qr/site-location.png"
                        alt="QR code — Aravi Eco Homes site location on Google Maps"
                        data-testid="contact-qr-image"
                        className="block w-44 h-44 md:w-52 md:h-52"
                      />
                    </div>
                    <p className="mt-6 text-[#F5F1E8]/70 text-xs font-light max-w-xs leading-relaxed">
                      Opens the exact site pin on Google Maps —
                      Ibrahimpatnam, Telangana. 17.089°N · 78.743°E.
                    </p>
                    <a
                      href="/qr/site-location.png"
                      download="aravi-site-location.png"
                      data-testid="contact-qr-download"
                      className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C9A458] hover:text-[#F5F1E8] transition-colors"
                    >
                      <Download size={12} strokeWidth={1.6} />
                      Save QR Code
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="mt-10 overflow-hidden border border-[#C9A458]/30">
              <iframe
                title="Aravi Eco Homes Site Location"
                src="https://www.google.com/maps?q=17.089022,78.743076&z=14&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-[420px] block"
                style={{ border: 0, filter: 'saturate(0.85) contrast(1.02)' }}
              />
            </div>
          </div>
        </div>
      </section>

      <Enquiry
        eyebrow="Send an Enquiry"
        heading={<>A few details<br />is all we need.</>}
        intro="Tell us which home calls you — The 605 or The 1210 — and we will write back personally within 24 hours."
      />
    </div>
  );
}

const Row = ({ icon, label, value, href }) => (
  <div className="flex items-start gap-4">
    <div className="w-9 h-9 grid place-items-center border border-[#C9A458]/40 text-[#A66B45] mt-0.5 shrink-0">
      {icon}
    </div>
    <div>
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#6D6A61]">{label}</div>
      {href ? (
        <a href={href} className="text-[#18342A] text-base mt-1 leading-tight font-light hover:text-[#A66B45] transition-colors block">
          {value}
        </a>
      ) : (
        <div className="text-[#18342A] text-base mt-1 leading-tight font-light">{value}</div>
      )}
    </div>
  </div>
);
